import { useEffect, useRef, useState } from 'react'
import { useThrottleFn } from '@react-cmpt/use-throttle'
import { Tile } from './penrose/tile'
import { TileKind } from './penrose/tilekind'
import { Constants } from './penrose/constants'
import { TilingPro } from './penrose/tilingpro'
import { PositionXY } from '../../../types/PositionXY'
import { getPagePositionXY } from '../../../utils/getPagePositionXY'
import { useContextMotifGenerator } from '../../../contexts/motifGeneratorContext'
import { useContextSelectedMotifs } from '../../../contexts/selectedMotifContext'
import { useContextSnackbar } from '../../../contexts/snackbarContext'
import { useEnv } from '../../../utils/useEnv'
import { InfoSnackbarType } from '../../../constants/animationStates'
import { isAndroid, isIOS, isMobile } from 'react-device-detect'
import { Dimensions, JSONExportPlane, MotifGeneratorType } from '../types'
import { getTransformProperties } from '../util'
import { ExportLayoutModalResponse } from '../ExportLayoutModal'
import { isSafari as _isSafari, SAFARI_MAX_CANVAS_SIZE } from '../../../utils/safariCanvasUtils'
import { cn } from '../../../utils/cn'
const isAppleDevice = _isSafari || isIOS

const DEFAULT_IMAGE_SIZE = 120
const MIN_IMAGE_SIZE = 60
const MAX_IMAGE_SIZE = 150
const DEFAULT_ZOOM_MULTIPLIER = 50
const DEFAULT_ITERATION_NUMBER = 7
const RESOLUTION_MULTIPLIER = 2

const EXPORT_SIZE = 3000
const EXPORT_SUPERSAMPLING_MULTIPLIER = 3
const TILE_MULTIPLIER = 3
const DISPLAY_PREVIEW_ZOOM_FACTOR = 1.366
const DISPLAY_PREVIEW_REFERENCE_SIZE = EXPORT_SIZE
const MOBILE_CANVAS_EXPORT_COOLDOWN_MS = 3000

let penroseDownloadLock = false
type Props = {
  rotationAngleProp?: number
  imageSizeProp?: number
  exportResponse?: ExportLayoutModalResponse
  onExportComplete?: () => void
}

const PenroseGenerator = ({
  rotationAngleProp,
  imageSizeProp,
  exportResponse,
  onExportComplete,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { isKiosk, isWeb } = useEnv()
  const dragging = useRef<boolean>(false)
  const defaultRotationAngle = Math.PI / 2
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const lastPos = useRef<PositionXY>({ x: 0, y: 0 })
  const { selectedMotifs } = useContextSelectedMotifs()
  const [iterationNumber] = useState<number>(DEFAULT_ITERATION_NUMBER)
  const canvasToExportRef = useRef<HTMLCanvasElement>(null)
  const [imageSize, setImageSize] = useState<number>(imageSizeProp || DEFAULT_IMAGE_SIZE)
  const [rotationAngle, setRotationAngle] = useState(rotationAngleProp || defaultRotationAngle)
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth)
  const {
    setExportInvoked,
    startExport,
    setStartExport,
    exportGeneratorCoordinatesToJSON,
    handleExport,
    handleExportCanvas,
  } = useContextMotifGenerator()
  const [images, setImages] = useState<HTMLImageElement[]>([])
  const [imagesThumbnailSize, setImagesThumbnailSize] = useState<HTMLImageElement[]>([])
  const [, setImageLoadingIsFinished] = useState<boolean>(false)
  const [zoomMultiplier, setZoomMultiplier] = useState<number>(DEFAULT_ZOOM_MULTIPLIER)
  const { showSnackbar, hideSnackbar, disabledSnackbars, disableSnackbar } = useContextSnackbar()
  const previewCanvasLogicalSize = windowWidth < EXPORT_SIZE ? EXPORT_SIZE : windowWidth
  const jsonCoordinates = useRef<JSONExportPlane>({
    clipPlane: undefined,
    entries: [],
  })
  const exportInProgress = useRef<boolean>(false)
  const exportStartConsumedRef = useRef<boolean>(false)
  const lastDownloadAtRef = useRef<number>(0)
  const mobileCanvasExportInFlightRef = useRef<boolean>(false)

  useEffect(() => {
    if (!startExport) {
      exportStartConsumedRef.current = false
    }
  }, [startExport])

  const loadAllImages = async () => {
    if (selectedMotifs.length === 0) {
      setImages([])
      setImagesThumbnailSize([])
      setImageLoadingIsFinished(false)
      return
    }

    const loadImage = async (
      src: string | null,
      motifId: number,
      kind: 'full' | 'thumb',
    ): Promise<HTMLImageElement> => {
      if (!src) {
        throw new Error(`Missing ${kind} image source for motif ${motifId}`)
      }

      const img = new Image()
      img.crossOrigin = 'anonymous'

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error(`${kind} failed for motif ${motifId}: ${src}`))
        img.src = src
      })

      await img.decode()
      return img
    }

    const regularImages = await Promise.all(
      selectedMotifs.map(motif => loadImage(motif.vectorizedUrl, motif.id, 'full')),
    )

    const thumbnailImages = await Promise.all(
      selectedMotifs.map(async (motif, index) => {
        try {
          return await loadImage(motif.vectorizedUrlXs ?? motif.vectorizedUrl, motif.id, 'thumb')
        } catch (error) {
          console.error(error)
          return regularImages[index]
        }
      }),
    )

    setImages(regularImages)
    setImagesThumbnailSize(thumbnailImages)
    setImageLoadingIsFinished(true)
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    hideSnackbar()
    if (!disabledSnackbars.includes(InfoSnackbarType.PENROSE)) {
      if (isKiosk) showSnackbar('snackbarMessages.usePenroseKiosk')
      if (isWeb) showSnackbar('snackbarMessages.usePenroseWeb')
      if (isMobile) showSnackbar('snackbarMessages.usePenroseMobile')
    }
    return () => {
      window.removeEventListener('resize', handleResize)
      hideSnackbar()
      disableSnackbar(InfoSnackbarType.PENROSE)
    }
  }, [])

  useEffect(() => {
    loadAllImages()
  }, [selectedMotifs])

  // Prevents pull-to refresh behavior on iOS devices
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let prevent = false

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return
      const scrollY =
        window.pageYOffset || document.body.scrollTop || document.documentElement.scrollTop
      prevent = scrollY === 0
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (prevent) {
        prevent = false
        e.preventDefault()
      }
    }

    container.addEventListener('touchstart', handleTouchStart)
    container.addEventListener('touchmove', handleTouchMove)

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
    }
  }, [])

  useEffect(
    () =>
      void (async () => {
        if (
          !canvasToExportRef.current ||
          !startExport ||
          !canvasRef.current ||
          exportInProgress.current ||
          exportStartConsumedRef.current
        )
          return

        exportStartConsumedRef.current = true
        exportInProgress.current = true
        // Consume export trigger immediately to avoid duplicate runs while async export is in progress.
        setStartExport(false)

        exportGeneratorCoordinatesToJSON({
          props: {
            type: MotifGeneratorType.PENROSE,
            selectedMotifs,
            rotationAngle,
            imageSize,
            zoomMultiplier,
          },
          planes: [jsonCoordinates.current],
          canvasSize: {
            width: canvasRef.current.width,
            height: canvasRef.current.height,
          },
        })

        const isTiled = exportResponse === ExportLayoutModalResponse.TILES
        const exportCtx = canvasToExportRef.current.getContext('2d')
        if (!exportCtx) return

        canvasToExportRef.current.width = EXPORT_SIZE
        canvasToExportRef.current.height = EXPORT_SIZE
        exportCtx.imageSmoothingQuality = 'high'
        exportCtx.imageSmoothingEnabled = true
        exportCtx.fillStyle = '#fff'
        exportCtx.fillRect(0, 0, EXPORT_SIZE, EXPORT_SIZE)

        const exportRenderSizeBase = EXPORT_SIZE * EXPORT_SUPERSAMPLING_MULTIPLIER
        const exportRenderSize = isAppleDevice
          ? Math.min(exportRenderSizeBase, SAFARI_MAX_CANVAS_SIZE)
          : exportRenderSizeBase
        const normalizedPreviewZoom = zoomMultiplier * DISPLAY_PREVIEW_ZOOM_FACTOR
        const exportRenderZoom = normalizedPreviewZoom * (exportRenderSize / EXPORT_SIZE)

        const tempCanvas = document.createElement('canvas')
        tempCanvas.width = exportRenderSize
        tempCanvas.height = exportRenderSize
        const tempCtx = tempCanvas.getContext('2d')
        if (!tempCtx) return

        tempCtx.save()
        tempCtx.translate(exportRenderSize / 2, exportRenderSize / 2)

        draw(tempCanvas, exportRenderZoom, {
          width: exportRenderSize,
          height: exportRenderSize,
        })
        tempCtx.restore()

        if (isTiled) {
          exportCtx.drawImage(
            tempCanvas,
            0,
            0,
            exportRenderSize,
            exportRenderSize,
            0,
            0,
            EXPORT_SIZE,
            EXPORT_SIZE,
          )
        } else {
          const sourceSize = exportRenderSize / 2
          const sourceX = exportRenderSize - sourceSize
          const sourceY = exportRenderSize - sourceSize
          exportCtx.drawImage(
            tempCanvas,
            sourceX,
            sourceY,
            sourceSize,
            sourceSize,
            0,
            0,
            EXPORT_SIZE,
            EXPORT_SIZE,
          )
        }

        if (!penroseDownloadLock) {
          const now = Date.now()
          if (now - lastDownloadAtRef.current < 2000) {
            exportInProgress.current = false
            setExportInvoked(false)
            onExportComplete?.()
            return
          }
          lastDownloadAtRef.current = now

          penroseDownloadLock = true
          if (isIOS || isAndroid) {
            if (mobileCanvasExportInFlightRef.current) {
              penroseDownloadLock = false
              exportInProgress.current = false
              setExportInvoked(false)
              onExportComplete?.()
              return
            }

            mobileCanvasExportInFlightRef.current = true
            handleExportCanvas(canvasToExportRef.current)
            window.setTimeout(() => {
              mobileCanvasExportInFlightRef.current = false
              penroseDownloadLock = false
            }, MOBILE_CANVAS_EXPORT_COOLDOWN_MS)
          } else {
            await handleExport(canvasToExportRef.current!).catch(console.error)
            penroseDownloadLock = false
          }
        }

        exportInProgress.current = false
        setExportInvoked(false)
        onExportComplete?.()
      })(),
    [
      startExport,
      onExportComplete,
      selectedMotifs,
      rotationAngle,
      imageSize,
      zoomMultiplier,
      handleExport,
      handleExportCanvas,
      exportGeneratorCoordinatesToJSON,
      setStartExport,
      setExportInvoked,
    ],
  )

  useEffect(() => {
    setZoomMultiplier(DEFAULT_ZOOM_MULTIPLIER)
  }, [windowWidth])

  useEffect(() => {
    if (!canvasRef.current) return

    draw(canvasRef.current, zoomMultiplier)
  }, [
    windowWidth,
    zoomMultiplier,
    images,
    imagesThumbnailSize,
    rotationAngle,
    imageSize,
    selectedMotifs,
  ])

  const draw = (canvas: HTMLCanvasElement, zoomMultiplier: number, size?: Dimensions) => {
    if (
      images.length !== selectedMotifs.length ||
      imagesThumbnailSize.length !== selectedMotifs.length
    )
      return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const isExportRender = size !== undefined
    const isExportOrSafari = isExportRender
    let appliedResolutionMultiplier = RESOLUTION_MULTIPLIER

    if (!size) {
      if (isAppleDevice) {
        const logicalSize = window.innerWidth < EXPORT_SIZE ? EXPORT_SIZE : window.innerWidth
        appliedResolutionMultiplier = Math.min(
          RESOLUTION_MULTIPLIER,
          SAFARI_MAX_CANVAS_SIZE / logicalSize,
        )

        canvas.width = Math.floor(logicalSize * appliedResolutionMultiplier)
        canvas.height = Math.floor(logicalSize * appliedResolutionMultiplier)
      } else {
        canvas.width =
          window.innerWidth < EXPORT_SIZE
            ? EXPORT_SIZE * RESOLUTION_MULTIPLIER
            : window.innerWidth * RESOLUTION_MULTIPLIER
        canvas.height =
          window.innerWidth < EXPORT_SIZE
            ? EXPORT_SIZE * RESOLUTION_MULTIPLIER
            : window.innerWidth * RESOLUTION_MULTIPLIER
      }
    } else {
      canvas.width = size.width
      canvas.height = size.height
      appliedResolutionMultiplier = 1
    }

    ctx.fillStyle = '#fff'
    ctx.strokeStyle = '#906090'
    ctx.lineWidth = 0.5
    ctx.imageSmoothingQuality = 'high'
    ctx.imageSmoothingEnabled = true

    const logicalCanvasWidth = canvas.width / appliedResolutionMultiplier
    const previewSizeNormalization = isExportRender
      ? 1
      : logicalCanvasWidth / DISPLAY_PREVIEW_REFERENCE_SIZE
    const effectiveZoom =
      zoomMultiplier * (isExportRender ? 1 : DISPLAY_PREVIEW_ZOOM_FACTOR * previewSizeNormalization)
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    const offsetXInPixels = 0
    const offsetYInPixels = 0

    ctx.setTransform(
      appliedResolutionMultiplier,
      0,
      0,
      -appliedResolutionMultiplier,
      canvas.width / 2 + offsetXInPixels,
      canvas.height / 2 + offsetYInPixels,
    )

    const tilingPro = new TilingPro().deflate(iterationNumber)
    const tessellation = tilingPro.getHalfTiles().findRhombs().normalize()
    const rhombs = tessellation.findRhombs().normalize()

    jsonCoordinates.current.entries = []
    Array.from(rhombs).forEach((rhomb: Tile) => {
      const translateX =
        rhomb.kind.value === TileKind.THIN_RHOMB.value
          ? effectiveZoom / 2
          : Math.cos(Math.PI / 5) * Constants.PHI * effectiveZoom

      const finalImageSize =
        rhomb.kind.value === TileKind.THICK_RHOMB.value
          ? imageSize * 1.6 * (effectiveZoom / 100)
          : imageSize * (effectiveZoom / 100)

      const thumbnailUsageLimit = isMobile ? finalImageSize * 1.8 : Number.POSITIVE_INFINITY

      let image = isExportOrSafari
        ? images[0]
        : thumbnailUsageLimit < imagesThumbnailSize[0].width
        ? imagesThumbnailSize[0]
        : images[0]
      let imageIdx = 0

      if (selectedMotifs.length === 2) {
        image = rhomb.kind.value === TileKind.THICK_RHOMB.value ? images[0] : images[1]
        imageIdx = rhomb.kind.value === TileKind.THICK_RHOMB.value ? 0 : 1
      }

      const dist = Math.sqrt(Math.pow(rhomb.origin.x, 2) + Math.pow(rhomb.origin.y, 2))
      if (selectedMotifs.length === 3) {
        if (rhomb.kind.value === TileKind.THICK_RHOMB.value) {
          if (Math.trunc(dist / 4) % 2 === 1) {
            image = isExportOrSafari
              ? images[2]
              : thumbnailUsageLimit < imagesThumbnailSize[2].width
              ? imagesThumbnailSize[2]
              : images[2]
            imageIdx = 2
          } else {
            image = isExportOrSafari
              ? images[0]
              : thumbnailUsageLimit < imagesThumbnailSize[0].width
              ? imagesThumbnailSize[0]
              : images[0]
            imageIdx = 0
          }
        } else {
          image = isExportOrSafari
            ? images[1]
            : thumbnailUsageLimit < imagesThumbnailSize[1].width
            ? imagesThumbnailSize[1]
            : images[1]
          imageIdx = 1
        }
      }

      if (selectedMotifs.length === 4) {
        if (Math.trunc(dist / 4 - 1) % 2 === 1) {
          if (rhomb.kind.value === TileKind.THICK_RHOMB.value) {
            image = isExportOrSafari
              ? images[2]
              : thumbnailUsageLimit < imagesThumbnailSize[2].width
              ? imagesThumbnailSize[2]
              : images[2]
            imageIdx = 2
          } else {
            image = isExportOrSafari
              ? images[0]
              : thumbnailUsageLimit < imagesThumbnailSize[0].width
              ? imagesThumbnailSize[0]
              : images[0]
            imageIdx = 0
          }
        } else {
          if (rhomb.kind.value === TileKind.THICK_RHOMB.value) {
            image = isExportOrSafari
              ? images[3]
              : thumbnailUsageLimit < imagesThumbnailSize[3].width
              ? imagesThumbnailSize[3]
              : images[3]
            imageIdx = 3
          } else {
            image = isExportOrSafari
              ? images[1]
              : thumbnailUsageLimit < imagesThumbnailSize[1].width
              ? imagesThumbnailSize[1]
              : images[1]
            imageIdx = 1
          }
        }
      }
      const angle = rhomb.abscissa.angle()
      ctx.save()

      ctx.translate(rhomb.origin.x * effectiveZoom, rhomb.origin.y * effectiveZoom)
      ctx.rotate(angle)

      ctx.translate(translateX, 0)
      ctx.rotate(rotationAngle)

      ctx.scale(-1, 1)

      const { origin, scale, rotation } = getTransformProperties(ctx)
      jsonCoordinates.current.entries.push({
        id: selectedMotifs[imageIdx].id,
        x: origin.x,
        y: origin.y,
        size: finalImageSize * scale,
        rotation: rotation,
      })

      ctx.drawImage(image, -finalImageSize / 2, -finalImageSize / 2, finalImageSize, finalImageSize)
      ctx.restore()
    })
  }

  const handleResize = () => {
    setWindowWidth(window.innerWidth)
  }

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault()
    onStartEvent(event.pageX, event.pageY)
  }

  const handleTouchStart = (event: React.TouchEvent) => {
    onStartEvent(event.touches[0].pageX, event.touches[0].pageY)
  }

  const onStartEvent = (x: number, y: number) => {
    lastPos.current.x = x
    lastPos.current.y = y
    dragging.current = true
  }

  const handleMouseUp = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault()
    dragging.current = false
  }

  const handleMouseMove = (event: React.MouseEvent | React.TouchEvent): void => {
    if (dragging.current) {
      const pageXY = getPagePositionXY(event)
      const moveX = lastPos.current.x - pageXY.x
      const moveY = lastPos.current.y - pageXY.y

      lastPos.current = pageXY

      let limitedImageSize = moveY + imageSize
      if (limitedImageSize > MAX_IMAGE_SIZE) {
        limitedImageSize = MAX_IMAGE_SIZE
      } else if (limitedImageSize < MIN_IMAGE_SIZE) {
        limitedImageSize = MIN_IMAGE_SIZE
      }

      setImageSize(limitedImageSize)
      setRotationAngle(rotationAngle + moveX / 100)
    }
  }

  const { callback } = useThrottleFn(handleMouseMove, 40)

  return (
    <>
      <div
        className="absolute left-0 top-0 size-full bg-center"
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={callback}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={callback}
        onTouchEnd={handleMouseUp}
      >
        <canvas
          ref={canvasRef}
          className={cn(
            'canvastodraw',
            { 'safari-canvas min-h-[unset]': isAppleDevice },
            'absolute left-1/2 top-1/2 h-[100vw] !w-screen !min-w-[100vw] !max-w-[100vw] [transform:translate(-50%,-50%)]',
          )}
          width={
            isAppleDevice ? SAFARI_MAX_CANVAS_SIZE : previewCanvasLogicalSize * TILE_MULTIPLIER
          }
          height={
            isAppleDevice ? SAFARI_MAX_CANVAS_SIZE : previewCanvasLogicalSize * TILE_MULTIPLIER
          }
        ></canvas>
        <canvas
          className="canvas-to-export absolute left-0 top-0 z-[-1] bg-white"
          ref={canvasToExportRef}
          width={EXPORT_SIZE}
          height={EXPORT_SIZE}
        ></canvas>
      </div>
    </>
  )
}

export default PenroseGenerator
